import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { CREATE_EXIT_STRATEGY_TEMPLATE, UPDATE_EXIT_STRATEGY_TEMPLATE } from '../graphql/mutations/strategyTemplates';
import { GET_POSITIONS } from '../graphql/queries/positions';
import { GET_TOKEN_WITH_POSITION } from '../graphql/queries/tokens';
import { PREVIEW_EXIT_STRATEGY } from '../graphql/queries/exitStrategyPreview';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import ExitStrategySettingsForm from './ExitStrategySettingsForm';
import ExitStrategyPreviewPanel from './ExitStrategyPreviewPanel';
import { formatPrice, parseFloatFromString, formatNumberToReadable } from '../utils/formatters';
import { 
  calculateDistributedPercentages,
  DISTRIBUTION_CURVES 
} from '../utils/distributionCurves';
import toast from 'react-hot-toast';

const ExitStrategyTemplateModal = ({ template, onClose }) => {
  const isEditing = !!template;
  const { currentPortfolioId, currentPortfolio } = usePortfolioContext();
  
  // Form state
  const [name, setName] = useState('');
  const [targetMultiple, setTargetMultiple] = useState('3');
  const [baseSource, setBaseSource] = useState('avg');
  const [exitLevelsCount, setExitLevelsCount] = useState(3);
  const [exitLevelsSpread, setExitLevelsSpread] = useState(10);
  const [takeInitialsEnabled, setTakeInitialsEnabled] = useState(false);
  const [takeInitialsMultiple, setTakeInitialsMultiple] = useState('2');
  const [hodlEnabled, setHodlEnabled] = useState(false);
  const [hodlPercentage, setHodlPercentage] = useState(10);
  const [advancedDistributionEnabled, setAdvancedDistributionEnabled] = useState(false);
  const [distributionCurve, setDistributionCurve] = useState('BELL');
  const [distributionFocus, setDistributionFocus] = useState(50);
  const [distributionIntensity, setDistributionIntensity] = useState(50);
  
  const [saving, setSaving] = useState(false);
  
  // Preview state: 'btc' = BTC @ current price (hypothetical), 'position' = select a portfolio position
  const [previewSource, setPreviewSource] = useState('btc');
  const [selectedPositionId, setSelectedPositionId] = useState('');
  const [backendPreview, setBackendPreview] = useState(null);
  const [btcCurrentPrice, setBtcCurrentPrice] = useState(null); // Fetched when base source is "current"
  const previewDebounceRef = useRef(null);

  // Mutations
  const [createTemplate] = useMutation(CREATE_EXIT_STRATEGY_TEMPLATE);
  const [updateTemplate] = useMutation(UPDATE_EXIT_STRATEGY_TEMPLATE);

  // Lazy load positions only when user selects "Select position"
  const [getPositions, { data: positionsData, loading: positionsLoading }] = useLazyQuery(GET_POSITIONS, {
    fetchPolicy: 'network-only',
  });

  // Fetch BTC current price when base source is "current" (for hypothetical preview)
  const [fetchBtcPrice] = useLazyQuery(GET_TOKEN_WITH_POSITION, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const price = data?.tokenWithPosition?.token?.marketData?.currentPrice;
      const num = price != null ? parseFloatFromString(price) : null;
      setBtcCurrentPrice(Number.isFinite(num) && num > 0 ? num : null);
    },
    onError: () => setBtcCurrentPrice(null),
  });

  const sortedPositions = useMemo(() => {
    if (!positionsData?.positions) return [];
    return [...positionsData.positions]
      .filter(p => parseFloatFromString(p.holdingsValue) > 0)
      .sort((a, b) => parseFloatFromString(b.holdingsValue) - parseFloatFromString(a.holdingsValue));
  }, [positionsData]);

  const hasPositions = sortedPositions.length > 0;

  // When user switches to "Select position", load positions
  const handlePreviewSourceChange = useCallback((source) => {
    setPreviewSource(source);
    if (source === 'position' && !positionsData && currentPortfolioId) {
      getPositions({ variables: { portfolioId: currentPortfolioId } });
    }
  }, [positionsData, currentPortfolioId, getPositions]);

  // When positions load, select biggest by default; if no positions, switch back to BTC
  useEffect(() => {
    if (previewSource !== 'position' || !positionsData) return;
    if (sortedPositions.length > 0) {
      if (!selectedPositionId) setSelectedPositionId(sortedPositions[0].id);
    } else {
      setPreviewSource('btc');
      setSelectedPositionId('');
    }
  }, [previewSource, positionsData, sortedPositions, selectedPositionId]);

  // Fetch BTC current price when base source is "current" (for hypothetical)
  useEffect(() => {
    if (baseSource !== 'current' || previewSource !== 'btc') {
      setBtcCurrentPrice(null);
      return;
    }
    if (!currentPortfolioId) return;
    fetchBtcPrice({
      variables: { coingeckoSlug: 'bitcoin', portfolioId: currentPortfolioId },
    });
  }, [baseSource, previewSource, currentPortfolioId, fetchBtcPrice]);

  // Get selected position data
  const selectedPosition = useMemo(() => {
    if (!selectedPositionId || selectedPositionId === 'hypothetical') return null;
    return sortedPositions.find(p => p.id === selectedPositionId);
  }, [selectedPositionId, sortedPositions]);

  // Backend preview query
  const [fetchBackendPreview] = useLazyQuery(PREVIEW_EXIT_STRATEGY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.previewExitStrategy) {
        setBackendPreview(data.previewExitStrategy);
      }
    },
    onError: (err) => {
      console.error('Backend preview error:', err);
    }
  });

  // Debounced backend preview (only when preview source is a position)
  useEffect(() => {
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
    }
    
    if (previewSource !== 'position' || !selectedPosition || !currentPortfolioId) {
      setBackendPreview(null);
      return;
    }
    
    previewDebounceRef.current = setTimeout(() => {
      const params = {
        targetMultiple: String(targetMultiple),
        baseSource: baseSource === 'avg' ? 'AVG' : 'CURRENT',
        exitLevelsCount: exitLevelsCount,
        exitLevelsSpread: exitLevelsSpread,
        takeInitialsEnabled: takeInitialsEnabled,
        takeInitialsMultiple: takeInitialsEnabled ? String(takeInitialsMultiple || '') : null,
        hodlEnabled: hodlEnabled,
        hodlPercentage: hodlEnabled ? hodlPercentage : null,
        advancedDistributionEnabled: advancedDistributionEnabled,
        distributionCurve: advancedDistributionEnabled ? distributionCurve : null,
        distributionFocus: advancedDistributionEnabled ? distributionFocus : null,
        distributionIntensity: advancedDistributionEnabled ? distributionIntensity : null,
      };
      
      fetchBackendPreview({ 
        variables: {
          portfolioId: currentPortfolioId,
          tokenId: selectedPosition.token.id,
          params,
        }
      });
    }, 300);
    
    return () => {
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
      }
    };
  }, [
    previewSource, currentPortfolioId, selectedPosition,
    targetMultiple, baseSource, exitLevelsCount, exitLevelsSpread,
    takeInitialsEnabled, takeInitialsMultiple, hodlEnabled, hodlPercentage,
    advancedDistributionEnabled, distributionCurve, distributionFocus, distributionIntensity,
    fetchBackendPreview
  ]);

  // Generate hypothetical preview (frontend calculation) when preview source is BTC
  // Sample: 10k as average buy price per BTC when base=avg; when base=current use fetched BTC price
  const hypotheticalPreview = useMemo(() => {
    if (previewSource !== 'btc') return null;
    
    const currency = currentPortfolio?.currency || 'USD';
    const costPerToken = baseSource === 'avg'
      ? 10000
      : (btcCurrentPrice != null && btcCurrentPrice > 0 ? btcCurrentPrice : 100000);
    const invested = 10000; // Portfolio currency invested
    const holdings = invested / costPerToken;
    const basePrice = costPerToken;
    
    const targetMult = parseFloatFromString(targetMultiple) || 3;
    const targetPrice = basePrice * targetMult;
    
    // Calculate levels
    const numLevels = exitLevelsCount;
    const spread = exitLevelsSpread / 100;
    
    // Calculate price range
    const minPrice = targetPrice * (1 - spread);
    const maxPrice = targetPrice * (1 + spread);
    const priceStep = numLevels > 1 ? (maxPrice - minPrice) / (numLevels - 1) : 0;
    
    // Calculate distribution percentages
    const curve = advancedDistributionEnabled ? distributionCurve : 'EQUAL';
    const percentages = calculateDistributedPercentages(
      numLevels, 
      curve === 'EQUAL' ? DISTRIBUTION_CURVES.EQUAL : curve,
      distributionFocus,
      distributionIntensity
    );
    
    // Available holdings after HODL
    const availableHoldings = hodlEnabled ? holdings * (1 - hodlPercentage / 100) : holdings;
    
    // Build levels
    const levels = [];
    let totalProceeds = 0;
    let totalCost = 0;
    
    for (let i = 0; i < numLevels; i++) {
      const price = numLevels === 1 ? targetPrice : minPrice + (priceStep * i);
      const percentage = percentages[i] || (100 / numLevels);
      const amount = availableHoldings * (percentage / 100);
      const value = amount * price;
      const cost = amount * costPerToken;
      const roi = cost > 0 ? ((value - cost) / cost) * 100 : 0;
      
      levels.push({
        price: price.toString(),
        amount: amount.toString(),
        percentage: percentage.toString(),
        value: value.toString(),
        roi,
        belowCost: price < costPerToken,
      });
      
      totalProceeds += value;
      totalCost += cost;
    }
    
    return {
      levels,
      totalSellAmount: availableHoldings.toString(),
      totalProceeds: totalProceeds.toString(),
      costBasis: totalCost.toString(),
      estimatedPnL: (totalProceeds - totalCost).toString(),
      estimatedROI: totalCost > 0 ? ((totalProceeds - totalCost) / totalCost) * 100 : 0,
      levelsAtLoss: levels.filter(l => l.belowCost).length,
      amountAtLoss: '0',
      lossAmount: '0',
      averageCost: costPerToken.toString(),
      hypothetical: true,
      currency,
      btcPrice: costPerToken,
    };
  }, [
    previewSource, currentPortfolio, targetMultiple, baseSource, btcCurrentPrice,
    exitLevelsCount, exitLevelsSpread, advancedDistributionEnabled,
    distributionCurve, distributionFocus, distributionIntensity,
    hodlEnabled, hodlPercentage
  ]);

  // Active preview: hypothetical when BTC, backend when position selected
  const preview = previewSource === 'btc' ? hypotheticalPreview : backendPreview;

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Load template data if editing
  useEffect(() => {
    if (template) {
      setName(template.name || '');
      setTargetMultiple(template.targetMultiple || '3');
      setBaseSource(template.baseSource === 'AVG' ? 'avg' : 'current');
      setExitLevelsCount(template.exitLevelsCount || 3);
      setExitLevelsSpread(template.exitLevelsSpread || 10);
      setTakeInitialsEnabled(template.takeInitialsEnabled || false);
      setTakeInitialsMultiple(template.takeInitialsMultiple || '2');
      setHodlEnabled(template.hodlEnabled || false);
      setHodlPercentage(template.hodlPercentage || 10);
      setAdvancedDistributionEnabled(template.advancedDistributionEnabled || false);
      setDistributionCurve(template.distributionCurve || 'BELL');
      setDistributionFocus(template.distributionFocus || 50);
      setDistributionIntensity(template.distributionIntensity || 50);
    }
  }, [template]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    
    const input = {
      name: name.trim(),
      targetMultiple,
      baseSource: baseSource === 'avg' ? 'AVG' : 'CURRENT',
      exitLevelsCount,
      exitLevelsSpread,
      takeInitialsEnabled,
      takeInitialsMultiple: takeInitialsEnabled ? takeInitialsMultiple : null,
      hodlEnabled,
      hodlPercentage: hodlEnabled ? hodlPercentage : null,
      advancedDistributionEnabled,
      distributionCurve: advancedDistributionEnabled ? distributionCurve : null,
      distributionFocus: advancedDistributionEnabled ? distributionFocus : null,
      distributionIntensity: advancedDistributionEnabled ? distributionIntensity : null,
    };

    try {
      if (isEditing) {
        const { data } = await updateTemplate({ variables: { id: template.id, input } });
        if (data?.updateExitStrategyTemplate?.success) {
          toast.success('Template updated');
          onClose();
        } else {
          toast.error(data?.updateExitStrategyTemplate?.error || 'Failed to update');
        }
      } else {
        const { data } = await createTemplate({ variables: { input } });
        if (data?.createExitStrategyTemplate?.success) {
          toast.success('Template created');
          onClose();
        } else {
          toast.error(data?.createExitStrategyTemplate?.error || 'Failed to create');
        }
      }
    } catch (err) {
      toast.error(err.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  // Calculate computed values for display (never negative)
  const selectedBasePrice = useMemo(() => {
    if (previewSource === 'btc') {
      return hypotheticalPreview?.btcPrice ?? 100000;
    }
    if (!selectedPosition) return 0;
    const avgBuy = parseFloatFromString(selectedPosition.averageBuyPrice);
    const avgNet = parseFloatFromString(selectedPosition.averageNetCost);
    const currentPrice = parseFloatFromString(selectedPosition.token?.marketData?.currentPrice);
    const useAvg = baseSource === 'avg';
    const avgCost = (avgBuy > 0 ? avgBuy : avgNet);
    const value = useAvg ? (avgCost > 0 ? avgCost : currentPrice) : (currentPrice > 0 ? currentPrice : avgCost);
    return Math.max(0, value || currentPrice || 0);
  }, [selectedPosition, previewSource, hypotheticalPreview, baseSource]);

  const targetPrice = useMemo(() => {
    return selectedBasePrice * (parseFloatFromString(targetMultiple) || 0);
  }, [selectedBasePrice, targetMultiple]);

  // Form value and context for shared ExitStrategySettingsForm
  const formValue = useMemo(() => ({
    targetMultiple,
    baseSource,
    exitLevelsCount,
    exitLevelsSpread,
    takeInitialsEnabled,
    takeInitialsMultiple: takeInitialsMultiple || '',
    hodlEnabled,
    hodlPercentage,
    advancedDistributionEnabled,
    distributionCurve,
    distributionFocus,
    distributionIntensity,
  }), [
    targetMultiple, baseSource, exitLevelsCount, exitLevelsSpread,
    takeInitialsEnabled, takeInitialsMultiple, hodlEnabled, hodlPercentage,
    advancedDistributionEnabled, distributionCurve, distributionFocus, distributionIntensity,
  ]);

  const handleFormChange = useCallback((field, val) => {
    switch (field) {
      case 'targetMultiple': setTargetMultiple(val); break;
      case 'baseSource': setBaseSource(val); break;
      case 'exitLevelsCount': setExitLevelsCount(val); break;
      case 'exitLevelsSpread': setExitLevelsSpread(val); break;
      case 'takeInitialsEnabled': setTakeInitialsEnabled(val); break;
      case 'takeInitialsMultiple': setTakeInitialsMultiple(val); break;
      case 'hodlEnabled': setHodlEnabled(val); break;
      case 'hodlPercentage': setHodlPercentage(val); break;
      case 'advancedDistributionEnabled': setAdvancedDistributionEnabled(val); break;
      case 'distributionCurve': setDistributionCurve(val); break;
      case 'distributionFocus': setDistributionFocus(val); break;
      case 'distributionIntensity': setDistributionIntensity(val); break;
      default: break;
    }
  }, []);

  const formContext = useMemo(() => ({
    selectedBasePrice,
    targetPrice,
    showBaseSourceToggle: true,
    takeInitialsPrice: selectedBasePrice * (parseFloatFromString(takeInitialsMultiple) || 0),
    minTakeInitialsMultiple: 1.1,
    maxTakeInitialsMultiple: targetMultiple ? parseFloatFromString(targetMultiple) - 0.1 : 3,
    tokenTicker: selectedPosition?.token?.ticker || 'TOKEN',
  }), [
    selectedBasePrice, targetPrice, takeInitialsMultiple, targetMultiple,
    selectedPosition?.token?.ticker,
  ]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Exit Strategy Template' : 'New Exit Strategy Template'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Row 1: Template name (left) + Preview position selector (right). Same row height: input height on left, radios centered in same-height band on right. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Conservative 3x Exit"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Preview
                </label>
                {/* Fixed-height row matching input (py-2): keeps height stable when select appears, radios vertically centered */}
                <div className="min-h-[2.5rem] flex items-center flex-nowrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer shrink-0">
                    <input
                      type="radio"
                      name="previewSource"
                      checked={previewSource === 'btc'}
                      onChange={() => handlePreviewSourceChange('btc')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Sample BTC position</span>
                  </label>
                  <label className={`flex items-center gap-2 shrink-0 ${!hasPositions && positionsData ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                    <input
                      type="radio"
                      name="previewSource"
                      checked={previewSource === 'position'}
                      onChange={() => hasPositions || !positionsData ? handlePreviewSourceChange('position') : undefined}
                      disabled={positionsData && !hasPositions}
                      className="w-4 h-4"
                    />
                    {positionsLoading ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Loading…</span>
                    ) : positionsData && hasPositions ? (
                      <select
                        value={selectedPositionId}
                        onChange={(e) => setSelectedPositionId(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[120px] max-w-[200px]"
                      >
                        {sortedPositions.map(pos => (
                          <option key={pos.id} value={pos.id}>
                            {pos.token.ticker} ({formatPrice(parseFloatFromString(pos.holdingsValue))})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Select position</span>
                    )}
                  </label>
                </div>
                {previewSource === 'position' && positionsData && !hasPositions && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No positions in this portfolio.</p>
                )}
              </div>
            </div>

            {/* Row 2: Strategy form (left) + Preview panel with footer buttons (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <ExitStrategySettingsForm
                value={formValue}
                onChange={handleFormChange}
                context={formContext}
              />
              <div>
                <ExitStrategyPreviewPanel
                  title="Planned Transactions Preview"
                  levels={preview?.levels || []}
                  backendPreview={preview}
                  variant="compact"
                  context={{
                    selectedBasePrice,
                    targetMultiple,
                    hodlEnabled,
                    hodlPercentage,
                    takeInitialsEnabled,
                  }}
                  tokenTicker={previewSource === 'btc' ? 'BTC' : (selectedPosition?.token?.ticker || 'TOKEN')}
                >
                  <div />
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : isEditing ? 'Update Template' : 'Create Template'}
                    </button>
                  </div>
                </ExitStrategyPreviewPanel>
                {preview?.hypothetical && (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                    *Hypothetical: {preview.currency} 10,000 invested in BTC at ${formatNumberToReadable(preview.btcPrice)}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExitStrategyTemplateModal;
